import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Chip,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
  Menu,
  FormControlLabel,
  Switch,
  Alert,
  Tooltip,
  Snackbar,
  useTheme,
  useMediaQuery,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Checkbox,
  Autocomplete,
  CircularProgress,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  DeleteForever as DeleteForeverIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Clear as ClearIcon,
  FilterList as FilterListIcon,
  Search as SearchIcon,
  NotificationsActive as NotifyIcon,
  ArrowDropDown as ArrowDropDownIcon,
  Person as PersonIcon,
  PersonAdd as PersonAddIcon,
  PersonOff as PersonOffIcon,
  PersonRemove as PersonRemoveIcon,
  Business as BusinessIcon,
  Groups as LinkedPeopleIcon,
} from '@mui/icons-material';
import { useSearchParams } from 'react-router-dom';
import { verificationsApi } from 'shared/services/verificationsApi';
import { companyRelationshipApi, CompanyRelationship } from 'shared/services/companyRelationshipApi';
import { userApi } from 'shared/services/userApi';
import { useAuth } from 'shared/contexts/AuthContext';
import { UserRole } from 'shared/types';
import { useStrings } from 'shared/utils/stringService';
import { DataTable, ColumnDef, ActionItem } from 'shared/components/layout/DataTable';
import { SkeletonLoader } from 'shared/components';
import { NotifyVerificationDialog } from './NotifyVerificationDialog';
import { useCompany } from 'shared/hooks/queries/useCompanies';
import { useProviderSelfServiceStatus } from 'shared/hooks/queries/useProviderSelfServiceStatus';
import {
  collapsibleSearchSx,
  getSearchButtonSx,
  primaryButtonSx,
  expandingButtonSx,
} from 'shared/theme/tableStyles';

interface CompanyVerificationsProps {
  companyId: string;
  companyName?: string;
  /** Bump this number from a parent to force the table to re-fetch (e.g.
   *  after another card on the same page approved/rejected a submission). */
  refreshKey?: number;
  /** Fired after a successful create / update / delete so the parent can
   *  re-sync sibling components (analytics card, review queue) that
   *  depend on the rule set. */
  onChanged?: () => void;
  /** From Company.settings.verifications.allowIndividualProviderVerifications
   *  (default false on the BE). While false, new provider-facing rules can
   *  only be created at company-level — the "Verificación Individual" menu
   *  option is disabled with an explanatory tooltip pointing at the toggle
   *  on the "De Cliente" tab. Defaults to true here so internal-only rule
   *  creation (appliesTo unaffected) is never blocked by omission. */
  allowIndividualProviderVerifications?: boolean;
  /** Restricts the displayed rows to one Nivel — the "De Empresa" /
   *  "De Cliente" tabs each show only rules at their own level (Empresa =
   *  whole-company provider documents, Cliente = per-person, own staff or
   *  provider). Omit to show every rule regardless of level. */
  levelFilter?: 'individual' | 'company';
  /** Page-level buttons (Descargar PDF, Enviar Verificación Masiva, switch
   *  to the sibling Usuarios/Proveedores page) rendered in the same header
   *  row as "Nueva Verificación", to its right — the parent page owns
   *  these actions, but the row itself (and its "icon-only, expands on
   *  hover" styling via expandingButtonSx) lives here so it's one single
   *  space-between row with filter/search on the far right, matching the
   *  Users.tsx header pattern instead of each page stacking its own strip
   *  of buttons above this component. */
  extraHeaderActions?: React.ReactNode;
}

interface Verification {
  _id: string;
  name: string;
  type: string;
  category?: string;
  description?: string;
  isRequired: boolean;
  validityPeriod?: number;
  /** When true, an approved submission auto-expires validityPeriod days
   *  after approval and the user must resubmit — a recurring requirement
   *  (e.g. "medical exam every 90 days") instead of a one-time check. */
  requiresRenewal?: boolean;
  /** Vigencia recurrente por calendario ("el día 10 de cada mes") en vez
   *  de "N días desde la aprobación" (validityPeriod). Mutuamente
   *  excluyente con validityPeriod/requiresRenewal en esta UI — cuando
   *  está presente, manda sobre ambos al fijar el expiryDate. */
  recurrence?: { type: 'day_of_month'; day: number } | null;
  /** When set, requirement only enforced on employees who joined
   *  on/after this date. Null = applies to everyone. */
  appliesFromDate?: string | null;
  /** Audience scope. Missing reads as 'both'. */
  appliesTo?: 'internal' | 'provider' | 'both';
  /** When appliesTo includes 'provider', restricts the rule to these
   *  specific linked provider companies. Empty/absent = all providers. */
  scopedProviderCompanies?: string[];
  /** Punctual exemption — these providers never need to comply with this
   *  rule even if they'd otherwise match appliesTo/scopedProviderCompanies.
   *  Takes precedence over scopedProviderCompanies (exclusion always wins). */
  excludedProviderCompanyIds?: string[];
  /** Punctual per-user exemption. Only meaningful for verificationLevel
   *  'individual' rules (a company-level document has no per-worker
   *  pending). Managed via the "Excepciones de usuario" row action, not
   *  the create/edit form — see verificationsApi.toggleUserExclusion. */
  excludedUserIds?: string[];
  /** Punctual per-user forced inclusion — the opposite of excludedUserIds.
   *  Forces the rule onto a specific worker who wouldn't otherwise get it
   *  by scope (appliesTo/scopedProviderCompanies). Never overrides a role
   *  exemption or an explicit excludedUserIds/excludedProviderCompanyIds
   *  entry — see verificationRuleAppliesToUser on the BE. Managed via the
   *  "Agregar" tab of the "Personas vinculadas" dialog. */
  includedUserIds?: string[];
  /** Company-level counterpart of includedUserIds — forces a specific
   *  linked provider to become eligible for a verificationLevel:'company'
   *  rule even outside scopedProviderCompanies, so it can then be turned on
   *  for that relationship via companyRelationshipApi.updateVerificationSettings.
   *  See verificationRuleAppliesToProviderCompany on the BE. */
  includedProviderCompanyIds?: string[];
  /** When true, an externally-lent company_admin must also comply with
   *  this rule (overrides the otherwise-blanket company_admin exemption).
   *  Never affects the client's own home company_admin. */
  requireFromCompanyAdmin?: boolean;
  /** 'individual' (default) = a per-worker document, follows the usual
   *  personal-verification flow. 'company' = a single whole-company
   *  document (e.g. business license) — never an individual's personal
   *  to-do; forces appliesTo:'provider' and is only ever submitted/reviewed
   *  via the relationship-level flow (RelationshipVerificationsCard). */
  verificationLevel?: 'individual' | 'company';
  /** Absolute deadline for the requirement itself. After this date the
   *  rule is retired (still rendered in lists for traceability, but the
   *  per-user enforcement layer should treat it as no longer required).
   *  Null = no deadline. Distinct from `validityPeriod`, which controls
   *  how long an individual user's submission is good for. */
  expiresOn?: string | null;
  /** False while a rule is in soft-deleted state (eligible for hard
   *  delete after 72hrs). True is the active default. */
  isActive?: boolean;
  /** Timestamp set when the rule was soft-deleted. Drives the 72hr
   *  grace countdown surfaced in the row. Cleared on reactivation. */
  deactivatedAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

const verificationTypes = [
  { value: 'document', label: 'Documento' },
  { value: 'course', label: 'Curso' },
  { value: 'certification', label: 'Certificación' },
  { value: 'training', label: 'Entrenamiento' },
  { value: 'medical_exam', label: 'Examen Médico' },
  { value: 'background_check', label: 'Verificación de Antecedentes' },
  { value: 'other', label: 'Otro' }
];

const verificationCategories = [
  'Seguridad',
  'Salud',
  'Calidad',
  'Medio Ambiente',
  'Legal',
  'Técnico',
  'Administrativo',
  'Otro'
];

export const CompanyVerifications: React.FC<CompanyVerificationsProps> = ({ companyId, refreshKey, onChanged, allowIndividualProviderVerifications = true, levelFilter, extraHeaderActions }) => {
  const { hasRole } = useAuth();
  const theme = useTheme();
  const strings = useStrings();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));

  const [verifications, setVerifications] = useState<Verification[]>([]);
  const [filteredVerifications, setFilteredVerifications] = useState<Verification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingVerification, setEditingVerification] = useState<Verification | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [verificationToDelete, setVerificationToDelete] = useState<Verification | null>(null);
  const [notifyDialogVerification, setNotifyDialogVerification] = useState<Verification | null>(null);
  // Hard-delete (permanent) flow — split from the soft-delete dialog
  // because the warnings + confirmation gate are stronger.
  const [hardDeleteDialog, setHardDeleteDialog] = useState<{
    open: boolean;
    verification: Verification | null;
    note: string;
    confirmText: string;
    forceBypass: boolean;
    submitting: boolean;
    error: string | null;
  }>({
    open: false,
    verification: null,
    note: '',
    confirmText: '',
    forceBypass: false,
    submitting: false,
    error: null,
  });
  // Hard delete is company-scoped for company_admin (the BE only lets them
  // touch their own company's rules) — but skipping the 72hr grace period
  // via "force" stays reserved to platform staff, see canForceHardDelete.
  const canHardDelete = hasRole([UserRole.SUPER_ADMIN, UserRole.SAFETY_STAFF, UserRole.COMPANY_ADMIN]);
  const canForceHardDelete = hasRole([UserRole.SUPER_ADMIN, UserRole.SAFETY_STAFF]);

  // A PROVEEDOR company with no plan of its own can't define its own
  // verification rules — same "own-company management" gate as inviting
  // subcontractors / hosting visitas (see providerSelfServiceGate.ts on the
  // BE, enforced in createCompanyVerification). Editing/deactivating
  // existing rules stays open — this only blocks creating new ones.
  const { data: companyRes } = useCompany(companyId, !!companyId);
  const companyType = companyRes?.data?.companyType;
  const { hasOwnActivePlan } = useProviderSelfServiceStatus(companyId, companyType);
  const isPlanlessProvider = companyType === 'proveedor' && !hasOwnActivePlan;

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'info' | 'warning'>('success');

  const [formData, setFormData] = useState({
    name: '',
    type: 'document',
    category: '',
    description: '',
    isRequired: true,
    validityPeriod: 365,
    // When true, an approved submission auto-expires validityPeriod days
    // after approval and the user must resubmit (recurring requirement).
    requiresRenewal: false,
    // Calendar-day recurrence toggle — mutually exclusive with
    // validityPeriod/requiresRenewal in this form. recurrenceDay is only
    // meaningful while recurrenceEnabled is true.
    recurrenceEnabled: false,
    recurrenceDay: 10,
    // null  → applies to all current + future employees
    // 'YYYY-MM-DD' → applies only to users who joined on/after this date
    appliesFromDate: null as string | null,
    // Audience scope — internal-colaborador verifications are paused
    // platform-wide, so new rules default to 'provider' (externos only).
    appliesTo: 'provider' as 'internal' | 'provider' | 'both',
    // null → no absolute deadline. Set to retire the requirement on a
    // specific date (e.g. "OSHA cert mandatory until 2027-12-31").
    expiresOn: null as string | null,
    // Empty = applies to every linked provider. Only meaningful when
    // appliesTo !== 'internal'.
    scopedProviderCompanies: [] as string[],
    // Punctual exemption — these providers are excluded from the rule even
    // if scopedProviderCompanies would otherwise include them.
    excludedProviderCompanyIds: [] as string[],
    // Overrides the blanket company_admin exemption for THIS rule only,
    // and only for an externally-lent admin (never the client's own).
    requireFromCompanyAdmin: false,
    // 'company' = a single whole-company document, not a per-worker to-do.
    verificationLevel: 'individual' as 'individual' | 'company',
  });

  // "Solicitar ahora" — local-only UI state, never sent to the create API
  // directly. When set, handleSubmit does a follow-up
  // getVerificationSettings→updateVerificationSettings merge per selected
  // relationship to add the freshly-created rule to its requiredVerifications.
  const [requestNow, setRequestNow] = useState(false);
  const [requestNowRelationshipIds, setRequestNowRelationshipIds] = useState<string[]>([]);
  const toggleRequestNowRelationship = (relationshipId: string) => {
    setRequestNowRelationshipIds((prev) =>
      prev.includes(relationshipId)
        ? prev.filter((id) => id !== relationshipId)
        : [...prev, relationshipId],
    );
  };
  const [newMenuAnchor, setNewMenuAnchor] = useState<HTMLElement | null>(null);

  const [searchExpanded, setSearchExpanded] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterMenuAnchor, setFilterMenuAnchor] = useState<null | HTMLElement>(null);
  const [filters, setFilters] = useState({
    type: 'all',
    category: 'all',
    isRequired: 'all'
  });

  // `?type=internal|external` URL param scopes the requirements list to
  // the verifications that apply to that audience. Rows whose appliesTo is
  // unset or `'both'` are visible under either scope (they apply to
  // everyone). The Usuarios page uses the same param for its Internos /
  // Proveedores switch, so jumping between tabs preserves audience.
  //
  // Internal verifications are paused platform-wide (2026-08-25) and no
  // longer needed at all (2026-08-31) — a stale `?type=internal` link
  // (e.g. carried over from the Team tab's Internos view) is treated the
  // same as `external` here so this list never surfaces internal-only rows.
  const [audienceSearchParams] = useSearchParams();
  const rawAudienceParam = audienceSearchParams.get('type');
  const audienceParam = rawAudienceParam === 'internal' ? 'external' : rawAudienceParam;
  const audienceFilter: 'external' | null = audienceParam === 'external' ? 'external' : null;

  const canManageVerifications = () => {
    return hasRole([UserRole.SUPER_ADMIN, UserRole.SAFETY_STAFF, UserRole.COMPANY_ADMIN]);
  };

  // Active provider relationships for this company — source list for the
  // "which providers does this rule apply to" checkbox in the dialog.
  // Only consumed by the create/edit dialog's "Proveedores incluidos"
  // picker — fetched when the dialog actually opens (see handleOpenDialog)
  // rather than unconditionally on every mount of this component.
  const [providerRelationships, setProviderRelationships] = useState<CompanyRelationship[]>([]);
  const [loadingProviderRelationships, setLoadingProviderRelationships] = useState(false);
  const loadProviderRelationships = useCallback(async () => {
    if (!companyId) return;
    setLoadingProviderRelationships(true);
    try {
      const rels = await companyRelationshipApi.getForCompany(companyId, 'active');
      setProviderRelationships(rels);
    } catch {
      setProviderRelationships([]);
    } finally {
      setLoadingProviderRelationships(false);
    }
  }, [companyId]);

  const toggleScopedProvider = (providerId: string) => {
    setFormData((prev) => ({
      ...prev,
      scopedProviderCompanies: prev.scopedProviderCompanies.includes(providerId)
        ? prev.scopedProviderCompanies.filter((id) => id !== providerId)
        : [...prev.scopedProviderCompanies, providerId],
    }));
  };

  const toggleExcludedProvider = (providerId: string) => {
    setFormData((prev) => ({
      ...prev,
      excludedProviderCompanyIds: prev.excludedProviderCompanyIds.includes(providerId)
        ? prev.excludedProviderCompanyIds.filter((id) => id !== providerId)
        : [...prev.excludedProviderCompanyIds, providerId],
    }));
  };

  // "Excepciones de usuario" dialog — per-worker exemption for an
  // individual-level rule (verificationsApi.toggleUserExclusion). Separate
  // from the create/edit dialog since it targets specific people, not the
  // rule's own scoping fields.
  interface SimpleUserOption { _id: string; name: string; email?: string }
  const [userExclusionDialog, setUserExclusionDialog] = useState<{
    open: boolean;
    verification: Verification | null;
  }>({ open: false, verification: null });
  const [companyUsersForExclusion, setCompanyUsersForExclusion] = useState<SimpleUserOption[]>([]);
  const [loadingExclusionUsers, setLoadingExclusionUsers] = useState(false);
  const [selectedExcludedUsers, setSelectedExcludedUsers] = useState<SimpleUserOption[]>([]);
  const [togglingExcludedUserId, setTogglingExcludedUserId] = useState<string | null>(null);

  // Shared roster fetch — feeds both the "Excepciones de usuario" Autocomplete
  // (all users, marking who's excluded) and the "Agregar" tab of "Personas
  // vinculadas" (same roster, filtered there to who's not linked yet).
  const loadCompanyUsersRoster = useCallback(async (): Promise<SimpleUserOption[]> => {
    try {
      const res = await userApi.getSimple({ company: companyId, limit: 500 });
      const users: SimpleUserOption[] = (res?.data || []).map((u: any) => ({
        _id: u._id,
        name: [u.firstName, u.lastName].filter(Boolean).join(' ') || 'Sin nombre',
        email: u.email,
      }));
      setCompanyUsersForExclusion(users);
      return users;
    } catch {
      setCompanyUsersForExclusion([]);
      return [];
    }
  }, [companyId]);

  const openUserExclusionDialog = async (verification: Verification) => {
    setUserExclusionDialog({ open: true, verification });
    setLoadingExclusionUsers(true);
    try {
      const users = await loadCompanyUsersRoster();
      const excludedIds = new Set((verification.excludedUserIds || []).map(String));
      setSelectedExcludedUsers(users.filter((u) => excludedIds.has(u._id)));
    } finally {
      setLoadingExclusionUsers(false);
    }
  };

  const closeUserExclusionDialog = () => {
    setUserExclusionDialog({ open: false, verification: null });
    setCompanyUsersForExclusion([]);
    setSelectedExcludedUsers([]);
    // Resync the table's row data (excludedUserIds isn't rendered as a
    // column, but a re-open of this same dialog reads it from the row).
    loadVerifications();
    // If this was opened from "Personas vinculadas" (still open underneath),
    // refresh that list too so an add/remove shows up without closing both.
    if (linkedDialog.open && linkedDialog.verification) {
      openLinkedDialog(linkedDialog.verification);
    }
  };

  // "Excepciones de proveedor" — company-level counterpart of "Excepciones
  // de usuario" above. Lets you exclude/re-include specific linked
  // providers from a company-level rule (adding one back is just un-
  // excluding them) without touching scopedProviderCompanies, which
  // defines the rule's own default audience and is only editable from the
  // create/edit form.
  interface SimpleProviderOption { _id: string; name: string }
  const [providerExclusionDialog, setProviderExclusionDialog] = useState<{
    open: boolean;
    verification: Verification | null;
  }>({ open: false, verification: null });
  const [companyProvidersForExclusion, setCompanyProvidersForExclusion] = useState<SimpleProviderOption[]>([]);
  const [loadingExclusionProviders, setLoadingExclusionProviders] = useState(false);
  const [selectedExcludedProviders, setSelectedExcludedProviders] = useState<SimpleProviderOption[]>([]);
  const [togglingExcludedProviderId, setTogglingExcludedProviderId] = useState<string | null>(null);

  const openProviderExclusionDialog = async (verification: Verification) => {
    setProviderExclusionDialog({ open: true, verification });
    setLoadingExclusionProviders(true);
    try {
      const rels = await companyRelationshipApi.getForCompany(companyId, 'active');
      const providers: SimpleProviderOption[] = (Array.isArray(rels) ? rels : [])
        .map((rel) => (typeof rel.proveedorCompany === 'object' ? rel.proveedorCompany : null))
        .filter((c): c is { _id: string; name: string; companyType: string } => !!c)
        .map((c) => ({ _id: c._id, name: c.name }));
      setCompanyProvidersForExclusion(providers);
      const excludedIds = new Set((verification.excludedProviderCompanyIds || []).map(String));
      setSelectedExcludedProviders(providers.filter((p) => excludedIds.has(p._id)));
    } catch {
      setCompanyProvidersForExclusion([]);
      setSelectedExcludedProviders([]);
    } finally {
      setLoadingExclusionProviders(false);
    }
  };

  const closeProviderExclusionDialog = () => {
    setProviderExclusionDialog({ open: false, verification: null });
    setCompanyProvidersForExclusion([]);
    setSelectedExcludedProviders([]);
    loadVerifications();
    if (linkedDialog.open && linkedDialog.verification) {
      openLinkedDialog(linkedDialog.verification);
    }
  };

  const handleToggleExcludedProvider = async (provider: SimpleProviderOption, excluded: boolean) => {
    const verification = providerExclusionDialog.verification;
    if (!verification) return;
    setTogglingExcludedProviderId(provider._id);
    try {
      const current = new Set((verification.excludedProviderCompanyIds || []).map(String));
      if (excluded) current.add(provider._id); else current.delete(provider._id);
      const nextExcluded = Array.from(current);
      await verificationsApi.updateVerification(verification._id, { excludedProviderCompanyIds: nextExcluded });
      // Keep the in-memory verification (used again if the dialog re-opens
      // without a full reload) consistent with what was just saved.
      setProviderExclusionDialog((s) =>
        s.verification ? { ...s, verification: { ...s.verification, excludedProviderCompanyIds: nextExcluded } } : s,
      );
      setSelectedExcludedProviders((prev) =>
        excluded ? [...prev, provider] : prev.filter((p) => p._id !== provider._id),
      );
    } catch {
      showSnackbar('Error al actualizar la excepción', 'error');
    } finally {
      setTogglingExcludedProviderId(null);
    }
  };

  const handleToggleExcludedUser = async (user: SimpleUserOption, excluded: boolean) => {
    const verification = userExclusionDialog.verification;
    if (!verification) return;
    setTogglingExcludedUserId(user._id);
    try {
      await verificationsApi.toggleUserExclusion(companyId, verification._id, user._id, excluded);
      setSelectedExcludedUsers((prev) =>
        excluded ? [...prev, user] : prev.filter((u) => u._id !== user._id),
      );
    } catch {
      showSnackbar('Error al actualizar la excepción', 'error');
    } finally {
      setTogglingExcludedUserId(null);
    }
  };

  // "Personas/Proveedores vinculados" — opened by clicking a table row.
  // Shows who this rule currently applies to (computed server-side, not a
  // blank picker like the "Excepciones de usuario" row action above) with
  // a one-click "Quitar" that excludes them going forward. Individual-level
  // rules list people (cross-referencing each user's own
  // verificationSummary, which already applies verificationRuleAppliesToUser
  // server-side); company-level rules list the linked provider companies
  // instead (from the relationship-level aggregation), since a
  // whole-company document has no per-worker pending.
  interface LinkedEntity {
    id: string;
    name: string;
    email?: string;
    status: string;
    /** Only set for company-level (provider) rows — the CompanyRelationship
     *  this entry's "required" state actually lives on (see
     *  handleRemoveLinkedProvider/handleAddLinkedProvider). */
    relationshipId?: string;
  }
  const [linkedDialog, setLinkedDialog] = useState<{
    open: boolean;
    verification: Verification | null;
  }>({ open: false, verification: null });
  const [linkedLoading, setLinkedLoading] = useState(false);
  const [linkedIndividuals, setLinkedIndividuals] = useState<LinkedEntity[]>([]);
  const [linkedProviders, setLinkedProviders] = useState<LinkedEntity[]>([]);
  const [removingLinkedId, setRemovingLinkedId] = useState<string | null>(null);
  // 0 = "Vinculados" (current list), 1 = "Agregar" (search + add). Reset to
  // 0 every time the dialog opens for a (possibly different) row.
  const [linkedTab, setLinkedTab] = useState(0);
  const [addingLinkedId, setAddingLinkedId] = useState<string | null>(null);
  const [addPersonInput, setAddPersonInput] = useState('');
  const [addProviderInput, setAddProviderInput] = useState('');
  // Ids added (or removed, if they were added earlier in this same session)
  // during the current dialog session — drives the "Vinculados (N)" badge.
  // Reset on open/close, never touched by anything outside this dialog.
  const [newlyLinkedIds, setNewlyLinkedIds] = useState<Set<string>>(new Set());
  // Add/remove inside this dialog would otherwise call onChanged() per
  // action, which bumps the parent's refreshKey and reloads the whole
  // table (SkeletonLoader) behind the still-open dialog — visible as a
  // flicker on every single click. Defer it to one call when the dialog
  // closes instead.
  const linkedDialogDirtyRef = useRef(false);

  const LINKED_STATUS_LABEL: Record<string, string> = {
    not_submitted: 'Sin enviar',
    pending: 'Pendiente',
    in_review: 'En revisión',
    approved: 'Aprobado',
    rejected: 'Rechazado',
    expired: 'Vencido',
  };

  const openLinkedDialog = async (verification: Verification) => {
    // The exclusion dialogs re-call this on close to refresh the list
    // "still open underneath" (see closeUserExclusionDialog/
    // closeProviderExclusionDialog) — that's a re-entry, not a fresh open,
    // so it must not wipe the "Vinculados (N)" badge or the pending
    // onChanged flag from adds/removes made earlier in this same session.
    const isReopen = linkedDialog.open && linkedDialog.verification?._id === verification._id;
    setLinkedDialog({ open: true, verification });
    if (!isReopen) {
      setLinkedTab(0);
      setAddPersonInput('');
      setAddProviderInput('');
      setNewlyLinkedIds(new Set());
      linkedDialogDirtyRef.current = false;
    }
    setLinkedLoading(true);
    setLinkedIndividuals([]);
    setLinkedProviders([]);
    try {
      if (verification.verificationLevel === 'company') {
        // Also refresh the candidate list for the "Agregar" tab — same
        // active-relationships source as the create/edit dialog's
        // "Proveedores incluidos" picker.
        loadProviderRelationships();
        const items = await companyRelationshipApi.getClientProviderVerifications(companyId);
        setLinkedProviders(
          items
            .filter((it) => String(it.companyVerificationId) === String(verification._id))
            .map((it) => ({
              id: it.providerCompanyId,
              name: it.providerCompanyName,
              status: it.status,
              relationshipId: it.relationshipId,
            })),
        );
      } else {
        // Also refresh the roster for the "Agregar" tab.
        setLoadingExclusionUsers(true);
        loadCompanyUsersRoster().finally(() => setLoadingExclusionUsers(false));
        // getAll caps `limit` at 100 server-side (see userRoutes.ts) —
        // matches the cap VerificationsAnalytics.tsx already works within
        // for the same per-company roster.
        const res = await userApi.getAll({ companyId, limit: 100 } as any);
        const users: any[] = (res as any)?.data || [];
        const linked: LinkedEntity[] = [];
        users.forEach((u: any) => {
          const detail = (u.verificationSummary?.details || []).find(
            (d: any) => String(d.companyId) === String(companyId),
          );
          const match = (detail?.verifications || []).find(
            (v: any) => String(v.id) === String(verification._id),
          );
          if (match) {
            linked.push({
              id: String(u._id),
              name: [u.firstName, u.lastName].filter(Boolean).join(' ') || u.email || 'Sin nombre',
              email: u.email,
              status: match.status,
            });
          }
        });
        setLinkedIndividuals(linked);
      }
    } catch {
      showSnackbar('Error al cargar las personas vinculadas', 'error');
    } finally {
      setLinkedLoading(false);
    }
  };

  const closeLinkedDialog = () => {
    setLinkedDialog({ open: false, verification: null });
    setLinkedIndividuals([]);
    setLinkedProviders([]);
    setLinkedTab(0);
    setNewlyLinkedIds(new Set());
    // Re-sync the table (excludedUserIds/excludedProviderCompanyIds aren't
    // rendered as columns, but a re-open of this or the exclusions dialog
    // reads them from the row).
    loadVerifications();
    // Fire the deferred sibling refresh (analytics card, review queue) once,
    // instead of per add/remove click — see linkedDialogDirtyRef.
    if (linkedDialogDirtyRef.current) {
      linkedDialogDirtyRef.current = false;
      onChanged?.();
    }
  };

  const handleRemoveLinkedIndividual = async (userId: string) => {
    const verification = linkedDialog.verification;
    if (!verification) return;
    setRemovingLinkedId(userId);
    try {
      await verificationsApi.toggleUserExclusion(companyId, verification._id, userId, true);
      setLinkedIndividuals((prev) => prev.filter((p) => p.id !== userId));
      setNewlyLinkedIds((prev) => {
        if (!prev.has(userId)) return prev;
        const next = new Set(prev);
        next.delete(userId);
        return next;
      });
      showSnackbar('Persona removida de la verificación', 'success');
      // Excluding someone changes their per-company "missing" breakdown —
      // same reason every other mutation here calls onChanged (see
      // handleDelete/handleReactivate above). Deferred to dialog close
      // (see linkedDialogDirtyRef) so it doesn't reload the table behind
      // this still-open dialog on every click.
      linkedDialogDirtyRef.current = true;
    } catch {
      showSnackbar('Error al remover a la persona', 'error');
    } finally {
      setRemovingLinkedId(null);
    }
  };

  // Adds someone who wouldn't otherwise get this rule by scope — see
  // CompanyVerification.includedUserIds. "Agregar" tab of "Personas
  // vinculadas".
  const handleAddLinkedIndividual = async (user: SimpleUserOption) => {
    const verification = linkedDialog.verification;
    if (!verification) return;
    setAddingLinkedId(user._id);
    try {
      await verificationsApi.toggleUserInclusion(companyId, verification._id, user._id, true);
      setLinkedIndividuals((prev) =>
        prev.some((p) => p.id === user._id)
          ? prev
          : [...prev, { id: user._id, name: user.name, email: user.email, status: 'not_submitted' }],
      );
      setLinkedDialog((s) =>
        s.verification
          ? {
              ...s,
              verification: {
                ...s.verification,
                includedUserIds: Array.from(new Set([...(s.verification.includedUserIds || []), user._id])),
              },
            }
          : s,
      );
      setNewlyLinkedIds((prev) => new Set(prev).add(user._id));
      showSnackbar('Persona agregada a la verificación', 'success');
      linkedDialogDirtyRef.current = true;
    } catch (error: any) {
      showSnackbar(error?.error || error?.message || 'Error al agregar a la persona', 'error');
    } finally {
      setAddingLinkedId(null);
    }
  };

  // "Quitar" on a company-level rule's linked provider — the actual source
  // of truth for who's linked is CompanyRelationship.requiredVerifications
  // (see companyRelationshipApi.getClientProviderVerifications), not the
  // rule's own excludedProviderCompanyIds — so removal has to go through
  // updateVerificationSettings on that specific relationship.
  const handleRemoveLinkedProvider = async (item: LinkedEntity) => {
    const verification = linkedDialog.verification;
    if (!verification || !item.relationshipId) return;
    setRemovingLinkedId(item.id);
    try {
      await companyRelationshipApi.setRequiredVerification(item.relationshipId, verification._id, false);
      setLinkedProviders((prev) => prev.filter((p) => p.id !== item.id));
      setNewlyLinkedIds((prev) => {
        if (!prev.has(item.id)) return prev;
        const next = new Set(prev);
        next.delete(item.id);
        return next;
      });
      showSnackbar('Proveedor removido de la verificación', 'success');
      linkedDialogDirtyRef.current = true;
    } catch {
      showSnackbar('Error al remover al proveedor', 'error');
    } finally {
      setRemovingLinkedId(null);
    }
  };

  // Adds a linked provider to this company-level rule even if it's outside
  // the rule's default scope (includedProviderCompanyIds makes it eligible),
  // then turns the requirement on for that specific relationship — same
  // mechanism as "Solicitar ahora" in handleSubmit. "Agregar" tab of
  // "Proveedores vinculados".
  const handleAddLinkedProvider = async (rel: CompanyRelationship) => {
    const verification = linkedDialog.verification;
    const providerCompany = typeof rel.proveedorCompany === 'object' ? rel.proveedorCompany : null;
    if (!verification || !providerCompany) return;
    setAddingLinkedId(rel._id);
    try {
      const nextIncluded = Array.from(
        new Set([...(verification.includedProviderCompanyIds || []), providerCompany._id]),
      );
      await verificationsApi.updateVerification(verification._id, {
        includedProviderCompanyIds: nextIncluded,
      });
      await companyRelationshipApi.setRequiredVerification(rel._id, verification._id, true);
      setLinkedProviders((prev) =>
        prev.some((p) => p.id === providerCompany._id)
          ? prev
          : [...prev, { id: providerCompany._id, name: providerCompany.name, status: 'not_submitted', relationshipId: rel._id }],
      );
      setLinkedDialog((s) =>
        s.verification ? { ...s, verification: { ...s.verification, includedProviderCompanyIds: nextIncluded } } : s,
      );
      setNewlyLinkedIds((prev) => new Set(prev).add(providerCompany._id));
      showSnackbar('Proveedor agregado a la verificación', 'success');
      linkedDialogDirtyRef.current = true;
    } catch (error: any) {
      showSnackbar(error?.error || error?.message || 'Error al agregar al proveedor', 'error');
    } finally {
      setAddingLinkedId(null);
    }
  };

  const loadVerifications = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const companyData = await verificationsApi.getCompanyVerifications(companyId);

      setVerifications(companyData);
      // Filter synchronously with the freshest filter/level/audience
      // settings (via the ref, see below) instead of setting the
      // unfiltered list here and waiting for the debounced effect —
      // otherwise rows from every level/audience flash on screen mixed
      // together for ~300ms before narrowing down to this tab's level.
      setFilteredVerifications(computeFilteredVerificationsRef.current(companyData));
      setPage(0);
    } catch (error: unknown) {
      setError('Error al cargar las verificaciones');
      setVerifications([]);
      setFilteredVerifications([]);
    } finally {
      setLoading(false);
    }
    // Deliberately NOT depending on search/filter/level/audience state —
    // this should only refetch from the server when companyId/refreshKey
    // change, never when the user types in search or switches a filter
    // (that's the debounced effect below, which re-filters in memory).
     
  }, [companyId]);

  // Pure — takes the data to filter as a param instead of always reading
  // `verifications` state, so `loadVerifications` can filter the just-fetched
  // data synchronously (via the ref below) without waiting for `verifications`
  // state to actually update and a follow-up effect to fire.
  const computeFilteredVerifications = useCallback((data: Verification[]) => {
    let filtered = [...data];

    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(v =>
        v.name.toLowerCase().includes(search) ||
        v.description?.toLowerCase().includes(search) ||
        v.category?.toLowerCase().includes(search)
      );
    }

    if (filters.type !== 'all') {
      filtered = filtered.filter(v => v.type === filters.type);
    }

    if (filters.category !== 'all') {
      if (filters.category === 'none') {
        filtered = filtered.filter(v => !v.category);
      } else {
        filtered = filtered.filter(v => v.category === filters.category);
      }
    }

    if (filters.isRequired !== 'all') {
      const isRequired = filters.isRequired === 'required';
      filtered = filtered.filter(v => v.isRequired === isRequired);
    }

    // Audience scope — applies AFTER the user-facing filters because
    // it's URL-driven, not user-controlled inside the page. Only 'external'
    // is reachable (see audienceParam above — internal verifications are
    // retired, not just paused) — shows rows for provider employees
    // ('provider' or the unset/'both' default).
    if (audienceFilter === 'external') {
      filtered = filtered.filter((v) => {
        const scope = (v as Verification).appliesTo;
        return !scope || scope === 'provider' || scope === 'both';
      });
    }

    // Nivel — which tab (De Empresa / De Cliente) this row belongs to.
    // Missing verificationLevel defaults to 'individual' on the BE, so a
    // legacy row with no value is treated the same way here.
    if (levelFilter) {
      filtered = filtered.filter((v) => (v.verificationLevel || 'individual') === levelFilter);
    }

    return filtered;
  }, [searchTerm, filters, audienceFilter, levelFilter]);

  // Always holds the latest computeFilteredVerifications — lets
  // loadVerifications filter synchronously with current settings without
  // depending on them directly (which would cause an unwanted refetch from
  // the server every time the user types in search or flips a filter).
  const computeFilteredVerificationsRef = useRef(computeFilteredVerifications);
  useEffect(() => {
    computeFilteredVerificationsRef.current = computeFilteredVerifications;
  }, [computeFilteredVerifications]);

  useEffect(() => {
    loadVerifications();
    // Re-run when the parent bumps refreshKey (sibling action approved/rejected
    // a submission and wants the whole page to re-sync).
     
  }, [loadVerifications, refreshKey]);

  // Debounced re-filter for user-driven changes (typing in search, flipping
  // a filter dropdown) — data is already loaded by this point, so this only
  // ever narrows/widens the existing list, never shows unfiltered rows.
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setFilteredVerifications(computeFilteredVerifications(verifications));
      setPage(0);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchTerm, filters, verifications, computeFilteredVerifications]);

  const handleOpenDialog = (verification?: Verification, level: 'individual' | 'company' = 'individual') => {
    if (verification) {
      setEditingVerification(verification);
      setFormData({
        name: verification.name,
        type: verification.type,
        category: verification.category || '',
        description: verification.description || '',
        isRequired: verification.isRequired,
        validityPeriod: verification.validityPeriod || 365,
        requiresRenewal: !!verification.requiresRenewal,
        recurrenceEnabled: verification.recurrence?.type === 'day_of_month',
        recurrenceDay: verification.recurrence?.day || 10,
        appliesFromDate: verification.appliesFromDate
          ? verification.appliesFromDate.slice(0, 10)
          : null,
        appliesTo: verification.appliesTo || 'both',
        expiresOn: verification.expiresOn
          ? verification.expiresOn.slice(0, 10)
          : null,
        scopedProviderCompanies: verification.scopedProviderCompanies || [],
        excludedProviderCompanyIds: verification.excludedProviderCompanyIds || [],
        requireFromCompanyAdmin: !!verification.requireFromCompanyAdmin,
        verificationLevel: verification.verificationLevel === 'company' ? 'company' : 'individual',
      });
    } else {
      setEditingVerification(null);
      setFormData({
        name: '',
        type: 'document',
        category: '',
        description: '',
        isRequired: true,
        validityPeriod: 365,
        requiresRenewal: false,
        recurrenceEnabled: false,
        recurrenceDay: 10,
        appliesFromDate: null,
        appliesTo: 'provider',
        expiresOn: null,
        scopedProviderCompanies: [],
        excludedProviderCompanyIds: [],
        requireFromCompanyAdmin: false,
        verificationLevel: level,
      });
    }
    setRequestNow(false);
    setRequestNowRelationshipIds([]);
    loadProviderRelationships();
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingVerification(null);
  };

  const handleSubmit = async () => {
    try {
      const { recurrenceEnabled, recurrenceDay, ...restFormData } = formData;
      const dataToSubmit: any = {
        ...restFormData,
        validityPeriod: formData.type === 'background_check' ? undefined : formData.validityPeriod,
        recurrence: recurrenceEnabled ? { type: 'day_of_month', day: recurrenceDay } : null,
      };

      if (editingVerification) {
        await verificationsApi.updateVerification(editingVerification._id, dataToSubmit);
      } else {
        const created = await verificationsApi.createCompanyVerification(companyId, dataToSubmit);

        // "Solicitar ahora" — add the freshly-created rule to every chosen
        // provider relationship's requiredVerifications. setRequiredVerification
        // only $addToSet's this one id instead of reading+rewriting the whole
        // array, so it can't collaterally drop some other rule the relationship
        // already required (see setRelationshipRequiredVerification on the BE).
        if (
          formData.verificationLevel === 'company' &&
          requestNow &&
          requestNowRelationshipIds.length > 0 &&
          created?._id
        ) {
          const failedCount = (
            await Promise.all(
              requestNowRelationshipIds.map(async (relationshipId) => {
                try {
                  await companyRelationshipApi.setRequiredVerification(relationshipId, created._id, true);
                  return false;
                } catch {
                  return true;
                }
              }),
            )
          ).filter(Boolean).length;
          if (failedCount > 0) {
            showSnackbar(
              failedCount === requestNowRelationshipIds.length
                ? 'La verificación se creó, pero no se pudo solicitar a los proveedores elegidos. Hágalo desde "Configurar verificaciones".'
                : `La verificación se creó y se solicitó a ${requestNowRelationshipIds.length - failedCount} proveedor(es), pero falló para ${failedCount}. Revise "Configurar verificaciones" para los pendientes.`,
              'error',
            );
          }
        }
      }

      await loadVerifications();
      handleCloseDialog();
      setError('');
      showSnackbar(
        editingVerification ? 'Verificación actualizada exitosamente' : 'Verificación creada exitosamente',
        'success'
      );
      // Tell siblings (analytics, review queue) to refetch — newly
      // created / edited rules change every user's per-company "missing"
      // breakdown.
      onChanged?.();
    } catch (error: any) {
      // apiClient throws a plain ApiError ({success, error, statusCode}),
      // not an Error instance — the backend's message lives in `.error`,
      // not `.message`. Check both so this keeps working if a call site
      // ever throws a real Error instead.
      const message = error?.error || error?.message || 'Error al guardar la verificación';
      setError(message);
      showSnackbar(message, 'error');
    }
  };

  // Soft delete = deactivate. Sets isActive: false and starts the 72hr
  // grace window before the rule becomes eligible for hard delete.
  const handleDelete = async () => {
    if (!verificationToDelete) return;
    try {
      await verificationsApi.deactivateVerification(companyId, verificationToDelete._id);
      await loadVerifications();
      setDeleteDialogOpen(false);
      setVerificationToDelete(null);
      setError('');
      showSnackbar(
        'Verificación desactivada. Podrá borrarse definitivamente en 72 horas.',
        'success',
      );
      onChanged?.();
    } catch (error: unknown) {
      setError('Error al desactivar la verificación');
      showSnackbar('Error al desactivar la verificación', 'error');
    }
  };

  // Reactivate — restores isActive: true. The rule's original
  // isRequired flag is untouched, so compliance recomputes from where
  // it left off.
  const handleReactivate = async (row: Verification) => {
    try {
      await verificationsApi.reactivateVerification(companyId, row._id);
      await loadVerifications();
      showSnackbar('Verificación reactivada', 'success');
      onChanged?.();
    } catch (error: unknown) {
      showSnackbar('Error al reactivar la verificación', 'error');
    }
  };

  // Hard delete (permanent). Wipes rule + every submission + every
  // S3 file. Gated behind a confirmation dialog with "type the name
  // to confirm" and an explicit force-bypass for the 72hr grace.
  const handleHardDelete = async () => {
    const v = hardDeleteDialog.verification;
    if (!v) return;
    setHardDeleteDialog((s) => ({ ...s, submitting: true, error: null }));
    try {
      await verificationsApi.hardDeleteVerification(companyId, v._id, {
        note: hardDeleteDialog.note.trim() || undefined,
        force: hardDeleteDialog.forceBypass,
      });
      await loadVerifications();
      setHardDeleteDialog({
        open: false,
        verification: null,
        note: '',
        confirmText: '',
        forceBypass: false,
        submitting: false,
        error: null,
      });
      showSnackbar('Verificación eliminada definitivamente', 'success');
      onChanged?.();
    } catch (error: any) {
      setHardDeleteDialog((s) => ({
        ...s,
        submitting: false,
        error: error?.message || 'Error al borrar definitivamente',
      }));
    }
  };

  // "X horas / días restantes" copy for the grace countdown.
  const formatGraceRemaining = (deactivatedAt?: string | null): string => {
    if (!deactivatedAt) return '';
    const deactivatedMs = new Date(deactivatedAt).getTime();
    const elapsed = Date.now() - deactivatedMs;
    const GRACE = 72 * 60 * 60 * 1000;
    if (elapsed >= GRACE) return 'Lista para borrar permanentemente';
    const remainingMs = GRACE - elapsed;
    const hours = Math.ceil(remainingMs / (60 * 60 * 1000));
    if (hours <= 1) return 'Menos de 1 hora para borrar';
    if (hours < 24) return `${hours} horas para borrar`;
    const days = Math.ceil(hours / 24);
    return `${days} día${days === 1 ? '' : 's'} para borrar`;
  };

  const isGraceElapsed = (deactivatedAt?: string | null): boolean => {
    if (!deactivatedAt) return true;
    const elapsed = Date.now() - new Date(deactivatedAt).getTime();
    return elapsed >= 72 * 60 * 60 * 1000;
  };

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'info' | 'warning') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const uniqueCategories = useMemo(() => {
    const categories = new Set<string>();
    verifications.forEach(v => {
      if (v.category) categories.add(v.category);
    });
    return Array.from(categories).sort();
  }, [verifications]);

  const hasActiveFilters = () => {
    return searchTerm || filters.type !== 'all' || filters.category !== 'all' || filters.isRequired !== 'all';
  };

  const handleResetFilters = () => {
    setSearchTerm('');
    setFilters({
      type: 'all',
      category: 'all',
      isRequired: 'all'
    });
  };

  const columns: ColumnDef[] = [
    {
      id: 'name',
      label: 'Nombre',
      width: '25%',
      render: (row) => (
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography
              variant="subtitle2"
              fontWeight="medium"
              sx={{
                textDecoration: row.isActive === false ? 'line-through' : 'none',
                color:
                  row.isActive === false ? 'text.secondary' : 'inherit',
              }}
            >
              {row.name}
            </Typography>
            {row.isActive === false ? (
              <Chip
                label={
                  isGraceElapsed(row.deactivatedAt)
                    ? 'Desactivada · Lista para borrar'
                    : `Desactivada · ${formatGraceRemaining(row.deactivatedAt)}`
                }
                size="small"
                color={isGraceElapsed(row.deactivatedAt) ? 'error' : 'warning'}
                variant="outlined"
                sx={{ height: 20, fontSize: '0.7rem' }}
              />
            ) : null}
          </Box>
          {row.description && (
            <Typography variant="caption" color="text.secondary">
              {row.description}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      id: 'isRequired',
      label: 'Requerida',
      width: '12%',
      render: (row) => row.isRequired ? (
        <Chip
          label="Requerida"
          size="small"
          icon={<CheckCircleIcon />}
          sx={{
            bgcolor: (theme) => `${theme.palette.background.chipSuccess}15`,
            color: (theme) => theme.palette.background.chipSuccess,
            fontWeight: 500,
            '& .MuiChip-icon': {
              color: (theme) => theme.palette.background.chipSuccess
            }
          }}
        />
      ) : (
        <Chip
          label="Opcional"
          size="small"
          variant="outlined"
          sx={{
            borderColor: 'action.disabled',
            color: 'text.secondary'
          }}
        />
      ),
    },
    {
      id: 'validityPeriod',
      label: 'Vigencia',
      width: '24%',
      // Vigencia + Vence el share a column — the per-submission validity
      // rule and the rule's own absolute retirement date, stacked instead
      // of two separate columns.
      render: (row) => {
        const recurrence = (row as Verification).recurrence;
        const expiresOnStr = (row as Verification).expiresOn;
        const expiresOnDate = expiresOnStr ? new Date(expiresOnStr) : null;
        const expiresOnPast = expiresOnDate ? expiresOnDate.getTime() < Date.now() : false;

        const validityLine = recurrence?.type === 'day_of_month' ? (
          <Typography variant="body2">
            <strong>Día {recurrence.day} de cada mes</strong>
          </Typography>
        ) : row.validityPeriod ? (
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
            <Typography variant="body2">
              <strong>{row.validityPeriod} días</strong>
            </Typography>
            {(row as Verification).requiresRenewal && (
              <Chip label="Se repite" size="small" color="secondary" variant="outlined" />
            )}
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">Sin vencimiento</Typography>
        );

        return (
          <Box>
            {validityLine}
            {(recurrence?.type === 'day_of_month' || row.validityPeriod) && (
              <Tooltip title="Avisos automáticos a 1 mes, 1 semana, 72h, 48h, 24h y al vencer.">
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                  Avisos automáticos
                </Typography>
              </Tooltip>
            )}
            {expiresOnDate && (
              <Typography
                variant="caption"
                color={expiresOnPast ? 'error.main' : 'text.secondary'}
                sx={{ display: 'block', mt: 0.25, fontWeight: expiresOnPast ? 600 : 400 }}
              >
                Vence: {expiresOnDate.toLocaleDateString('es-CR', { year: 'numeric', month: 'short', day: 'numeric' })}
              </Typography>
            )}
          </Box>
        );
      },
    },
  ];

  // Per-row actions are state-aware. Active rules surface Edit +
  // Desactivar. Soft-deleted rules surface Reactivar + (for
  // super_admin / safety_staff only) Borrar permanentemente — the
  // latter is gated by the 72hr grace at the BE; the FE just shows
  // when the action will actually go through cleanly vs require the
  // force flag.
  const actions: ActionItem[] = canManageVerifications()
    ? [
        {
          id: 'edit',
          label: 'Editar',
          icon: <EditIcon fontSize="small" />,
          onClick: (row) => handleOpenDialog(row),
          hidden: (row: Verification) => row.isActive === false,
        },
        {
          id: 'notify',
          label: 'Notificar',
          icon: <NotifyIcon fontSize="small" />,
          onClick: (row) => setNotifyDialogVerification(row),
          hidden: (row: Verification) => row.isActive === false,
        },
        {
          id: 'user-exceptions',
          label: 'Excepciones de usuario',
          icon: <PersonOffIcon fontSize="small" />,
          onClick: (row) => openUserExclusionDialog(row),
          // Company-level rules have no per-worker pending — see
          // toggleUserVerificationExclusion's own guard on the BE.
          hidden: (row: Verification) => row.isActive === false || row.verificationLevel === 'company',
        },
        {
          id: 'provider-exceptions',
          label: 'Excepciones de proveedor',
          icon: <PersonOffIcon fontSize="small" />,
          onClick: (row) => openProviderExclusionDialog(row),
          // Only company-level rules have provider exclusions — individual
          // rules use "Excepciones de usuario" above instead.
          hidden: (row: Verification) => row.isActive === false || row.verificationLevel !== 'company',
        },
        {
          id: 'deactivate',
          label: 'Desactivar',
          icon: <DeleteIcon fontSize="small" />,
          color: 'warning',
          onClick: (row) => {
            setVerificationToDelete(row);
            setDeleteDialogOpen(true);
          },
          hidden: (row: Verification) => row.isActive === false,
        },
        {
          id: 'reactivate',
          label: 'Reactivar',
          icon: <RefreshIcon fontSize="small" />,
          color: 'success',
          onClick: (row) => handleReactivate(row),
          hidden: (row: Verification) => row.isActive !== false,
        },
        {
          id: 'hard-delete',
          label: 'Borrar permanentemente',
          icon: <DeleteForeverIcon fontSize="small" />,
          color: 'error',
          onClick: (row) =>
            setHardDeleteDialog({
              open: true,
              verification: row,
              note: '',
              confirmText: '',
              forceBypass: false,
              submitting: false,
              error: null,
            }),
          hidden: (row: Verification) => row.isActive !== false || !canHardDelete,
        },
      ]
    : [];

  if (loading) {
    return <SkeletonLoader variant={isMobile ? 'cards' : 'table'} rows={rowsPerPage} />;
  }

  return (
    <Box>
      {/* Header — one row, primary actions on the left and filter/search on
          the far right (space-between), same pattern as Users.tsx. Every
          left-side button is icon-only by default and expands to show its
          label on hover/focus (expandingButtonSx) so Nueva Verificación
          plus whatever page-level actions the parent passes in via
          extraHeaderActions (Descargar PDF, Enviar Verificación Masiva,
          switch page) all fit on one line instead of stacking. */}
      <Box sx={{
        mb: 3,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 1,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
          {/* Add Button — split into Individual / De Empresa, same pattern as
              "Solicitar Nuevo" / "Invitar Existente" for providers. Picking
              either preselects formData.verificationLevel before the dialog
              opens, so the distinction is obvious up front instead of buried
              in a switch mid-form. */}
          {canManageVerifications() && !isPlanlessProvider && (
            <>
              <Button
                variant="contained"
                onClick={(e) => setNewMenuAnchor(e.currentTarget)}
                sx={[primaryButtonSx as any, expandingButtonSx]}
              >
                <AddIcon fontSize="small" />
                <Box component="span" className="btn-expand-label">
                  Nueva Verificación
                  <ArrowDropDownIcon fontSize="small" />
                </Box>
              </Button>
              <Menu
                anchorEl={newMenuAnchor}
                open={!!newMenuAnchor}
                onClose={() => setNewMenuAnchor(null)}
              >
                <Tooltip
                  title={
                    allowIndividualProviderVerifications
                      ? ''
                      : 'Desactivado — actívalo en el switch de la pestaña "De Cliente" para pedir documentos a cada trabajador de un proveedor individualmente.'
                  }
                  disableHoverListener={allowIndividualProviderVerifications}
                >
                  <span>
                    <MenuItem
                      disabled={!allowIndividualProviderVerifications}
                      onClick={() => { setNewMenuAnchor(null); handleOpenDialog(undefined, 'individual'); }}
                    >
                      <ListItemIcon><PersonIcon fontSize="small" /></ListItemIcon>
                      <ListItemText
                        primary="Verificación Individual"
                        secondary="Documento de un trabajador"
                      />
                    </MenuItem>
                  </span>
                </Tooltip>
                <MenuItem
                  onClick={() => { setNewMenuAnchor(null); handleOpenDialog(undefined, 'company'); }}
                >
                  <ListItemIcon><BusinessIcon fontSize="small" /></ListItemIcon>
                  <ListItemText
                    primary="Verificación de Empresa"
                    secondary="Documento de toda la empresa proveedora"
                  />
                </MenuItem>
              </Menu>
            </>
          )}
          {extraHeaderActions}
        </Box>

        {/* Filter + search tools — always on the right */}
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
          {/* Filter Button */}
          <IconButton
            onClick={(e) => setFilterMenuAnchor(e.currentTarget)}
            sx={getSearchButtonSx(filters.type !== 'all' || filters.category !== 'all' || filters.isRequired !== 'all')}
          >
            <FilterListIcon />
          </IconButton>

          {/* Filter Menu */}
          <Menu
            anchorEl={filterMenuAnchor}
            open={Boolean(filterMenuAnchor)}
            onClose={() => setFilterMenuAnchor(null)}
            PaperProps={{
              sx: { p: 2, minWidth: 200 }
            }}
          >
            <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
              Filtros
            </Typography>

            <FormControl size="small" fullWidth sx={{ mb: 2 }}>
              <InputLabel>Tipo</InputLabel>
              <Select
                value={filters.type}
                onChange={(e) => setFilters({ ...filters, type: e.target.value })}
                label="Tipo"
              >
                <MenuItem value="all">Todos</MenuItem>
                {verificationTypes.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" fullWidth sx={{ mb: 2 }}>
              <InputLabel>Categoría</InputLabel>
              <Select
                value={filters.category}
                onChange={(e) => setFilters({ ...filters, category: e.target.value })}
                label="Categoría"
              >
                <MenuItem value="all">Todas</MenuItem>
                <MenuItem value="none">Sin categoría</MenuItem>
                {uniqueCategories.map((cat) => (
                  <MenuItem key={cat} value={cat}>
                    {cat}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" fullWidth>
              <InputLabel>Requerimiento</InputLabel>
              <Select
                value={filters.isRequired}
                onChange={(e) => setFilters({ ...filters, isRequired: e.target.value })}
                label="Requerimiento"
              >
                <MenuItem value="all">Todas</MenuItem>
                <MenuItem value="required">Requeridas</MenuItem>
                <MenuItem value="optional">Opcionales</MenuItem>
              </Select>
            </FormControl>

            {hasActiveFilters() && (
              <Button
                size="small"
                onClick={handleResetFilters}
                sx={{ mt: 2, width: '100%' }}
              >
                Limpiar filtros
              </Button>
            )}
          </Menu>

          {/* Collapsible Search */}
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Collapse in={searchExpanded} orientation="horizontal" timeout={300}>
              <TextField
                placeholder="Buscar verificaciones..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                size="small"
                autoFocus={searchExpanded}
                InputProps={{
                  endAdornment: searchTerm && (
                    <IconButton size="small" onClick={() => setSearchTerm('')}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  )
                }}
                sx={collapsibleSearchSx}
              />
            </Collapse>
            <IconButton
              onClick={() => {
                setSearchExpanded(!searchExpanded);
                if (searchExpanded) setSearchTerm('');
              }}
              sx={getSearchButtonSx(!!searchExpanded || !!searchTerm)}
            >
              <SearchIcon />
            </IconButton>
          </Box>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <DataTable
        columns={columns}
        rows={filteredVerifications}
        actions={actions}
        onRowClick={(row) => openLinkedDialog(row)}
        loading={loading}
        emptyMessage={
          hasActiveFilters()
            ? 'No se encontraron verificaciones con los filtros seleccionados'
            : `No hay verificaciones configuradas para este ${strings.sede}`
        }
        pagination={{
          page,
          rowsPerPage,
          total: filteredVerifications.length,
          onPageChange: setPage,
          onRowsPerPageChange: setRowsPerPage,
        }}
        striped={true}
        hover={true}
      />

      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        fullScreen={isXs}
        sx={{
          '& .MuiDialog-paper': {
            margin: { xs: 0, sm: '32px' },
            width: { xs: '100%', sm: 'auto' },
            maxHeight: { xs: '100%', sm: '90vh' }
          }
        }}
      >
        <DialogTitle>
          {editingVerification
            ? 'Editar Verificación'
            : formData.verificationLevel === 'company'
            ? 'Nueva Verificación de Empresa'
            : 'Nueva Verificación Individual'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {!editingVerification && (
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 1.5,
                  border: 1,
                  borderColor: formData.verificationLevel === 'company' ? 'secondary.main' : 'divider',
                  bgcolor: formData.verificationLevel === 'company' ? 'secondary.50' : 'transparent',
                }}
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.verificationLevel === 'company'}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          verificationLevel: e.target.checked ? 'company' : 'individual',
                        })
                      }
                    />
                  }
                  label={
                    formData.verificationLevel === 'company'
                      ? 'Verificación de la empresa (no de una persona)'
                      : 'Verificación individual (de un trabajador)'
                  }
                />
                <FormHelperText sx={{ mt: 0 }}>
                  {formData.verificationLevel === 'company'
                    ? 'Un documento único para toda la empresa proveedora (p. ej. licencia comercial, póliza de responsabilidad civil) — no aparecerá como pendiente para ningún trabajador individual. Se envía y revisa desde "Configurar verificaciones" en el detalle del proveedor.'
                    : 'Documento personal que cada trabajador (interno o de un proveedor) debe entregar.'}
                </FormHelperText>
              </Box>
            )}

            <TextField
              label="Nombre"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              fullWidth
              required
            />

            <FormControl fullWidth required>
              <InputLabel>Tipo</InputLabel>
              <Select
                value={formData.type}
                onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                label="Tipo"
              >
                {verificationTypes.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Categoría</InputLabel>
              <Select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                label="Categoría"
              >
                <MenuItem value="">Sin categoría</MenuItem>
                {verificationCategories.map((cat) => (
                  <MenuItem key={cat} value={cat}>
                    {cat}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Descripción"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              fullWidth
              multiline
              rows={3}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={formData.isRequired}
                  onChange={(e) => setFormData({ ...formData, isRequired: e.target.checked })}
                />
              }
              label={formData.isRequired ? 'Verificación obligatoria' : 'Verificación opcional'}
            />

            {/* "Solo nuevos empleados" — when on, the rule is only
                enforced for users who joined the company on or after
                the picked date. Existing employees (joined earlier)
                are grandfathered out. Defaults to today when toggled on.
                Meaningless for a company-level rule — there's no "employee"
                to grandfather, it's a single whole-company document. */}
            {formData.verificationLevel !== 'company' && (
              <Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!formData.appliesFromDate}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          appliesFromDate: e.target.checked
                            ? new Date().toISOString().slice(0, 10)
                            : null,
                        })
                      }
                    />
                  }
                  label="Aplica solo a nuevos empleados"
                />
                {formData.appliesFromDate && (
                  <TextField
                    type="date"
                    label="Fecha de corte"
                    value={formData.appliesFromDate}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        appliesFromDate: e.target.value || null,
                      })
                    }
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    helperText="Solo se exigirá a usuarios que se hayan unido en o después de esta fecha."
                    sx={{ mt: 1 }}
                  />
                )}
              </Box>
            )}

            {formData.type !== 'background_check' && (
              <>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.recurrenceEnabled}
                      onChange={(e) =>
                        setFormData({ ...formData, recurrenceEnabled: e.target.checked })
                      }
                    />
                  }
                  label="Vence un día fijo de cada mes (en vez de N días desde la aprobación)"
                />
                {formData.recurrenceEnabled ? (
                  <>
                    <TextField
                      label="Día del mes"
                      type="number"
                      value={formData.recurrenceDay}
                      onChange={(e) => {
                        const raw = parseInt(e.target.value) || 1;
                        setFormData({ ...formData, recurrenceDay: Math.min(31, Math.max(1, raw)) });
                      }}
                      fullWidth
                      inputProps={{ min: 1, max: 31 }}
                      helperText='Ej: 10 → vence todos los 10 de cada mes. Si un mes no tiene ese día (ej. 31 en febrero), cae en el último día de ese mes.'
                    />
                    <Alert severity="info" sx={{ mt: -1 }}>
                      Al aprobar una entrega, el vencimiento se fijará automáticamente en el
                      próximo día {formData.recurrenceDay} del calendario, sin importar cuándo
                      se apruebe.
                    </Alert>
                  </>
                ) : (
                  <>
                    <TextField
                      label="Período de validez (días)"
                      type="number"
                      value={formData.validityPeriod}
                      onChange={(e) => setFormData({ ...formData, validityPeriod: parseInt(e.target.value) || 0 })}
                      fullWidth
                      helperText="Número de días que la verificación es válida"
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.requiresRenewal}
                          onChange={(e) =>
                            setFormData({ ...formData, requiresRenewal: e.target.checked })
                          }
                        />
                      }
                      label="Se repite automáticamente"
                    />
                    {formData.requiresRenewal && (
                      <Alert severity="info" sx={{ mt: -1 }}>
                        Al aprobar una entrega, el vencimiento se calculará automáticamente
                        a {formData.validityPeriod || 0} días. Cuando venza, el usuario deberá
                        volver a presentarla — se le avisará antes (30, 7, 3, 2 y 1 día antes).
                      </Alert>
                    )}
                  </>
                )}
              </>
            )}

            {/* Absolute expiration of the REQUIREMENT itself (not of any
                individual submission). Distinct from validityPeriod: this
                is a fixed calendar date after which the rule is retired
                (e.g. "Carné OSHA es mandatorio hasta 31/12/2027"). */}
            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={!!formData.expiresOn}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        expiresOn: e.target.checked
                          ? new Date(Date.now() + 365 * 24 * 60 * 60 * 1000)
                              .toISOString()
                              .slice(0, 10)
                          : null,
                      })
                    }
                  />
                }
                label="Tiene fecha de vencimiento"
              />
              {formData.expiresOn && (
                <TextField
                  type="date"
                  label="Vence el"
                  value={formData.expiresOn}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      expiresOn: e.target.value || null,
                    })
                  }
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  helperText="Después de esta fecha el requisito se considera retirado. Deje el interruptor apagado para sin vencimiento."
                  sx={{ mt: 1 }}
                />
              )}
            </Box>

            {/* Audience scope selector removed — internal-colaborador
                verifications are paused platform-wide, so every new rule
                is created as 'provider' (externos) and the field is no
                longer user-editable. `formData.appliesTo` still round-trips
                unchanged when editing a legacy 'both'/'internal' rule since
                nothing here writes to it anymore. */}
            <Alert severity="info" variant="outlined">
              Aplica a: <strong>Solo de proveedores (externos)</strong>
            </Alert>

            {formData.appliesTo !== 'internal' && formData.verificationLevel !== 'company' && (
              <Box>
                <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                  Proveedores incluidos
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Deje todos sin marcar para aplicar a cualquier proveedor. Marque uno o más para limitar la regla solo a esos proveedores.
                </Typography>
                {providerRelationships.length === 0 ? (
                  <Alert severity="info" sx={{ mb: 1 }}>
                    Esta empresa no tiene proveedores activos vinculados.
                  </Alert>
                ) : (
                  <List dense sx={{ maxHeight: 220, overflow: 'auto', border: 1, borderColor: 'divider', borderRadius: 1 }}>
                    {providerRelationships.map((rel) => {
                      const provider = typeof rel.proveedorCompany === 'object' ? rel.proveedorCompany : null;
                      if (!provider) return null;
                      const checked = formData.scopedProviderCompanies.includes(provider._id);
                      return (
                        <ListItemButton key={provider._id} dense onClick={() => toggleScopedProvider(provider._id)}>
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <Checkbox edge="start" checked={checked} disableRipple />
                          </ListItemIcon>
                          <ListItemText primary={provider.name} />
                        </ListItemButton>
                      );
                    })}
                  </List>
                )}

                <FormControlLabel
                  sx={{ mt: 1.5 }}
                  control={
                    <Switch
                      checked={formData.requireFromCompanyAdmin}
                      onChange={(e) =>
                        setFormData({ ...formData, requireFromCompanyAdmin: e.target.checked })
                      }
                    />
                  }
                  label="Exigir también a Administradores externos"
                />
                <FormHelperText>
                  Un Administrador de Empresa que llega prestado por un proveedor deberá cumplir esta regla. No afecta a los Administradores de esta misma empresa.
                </FormHelperText>
              </Box>
            )}

            {/* Punctual per-provider exemption — applies at both levels
                (unlike "Proveedores incluidos", which only makes sense for
                individual-level scoping): a company-level rule can also
                exempt one specific provider without deactivating it for
                the rest of the client's providers. */}
            {formData.appliesTo !== 'internal' && (
              <Box>
                <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                  Proveedores excluidos
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Marque uno o más proveedores para eximirlos de esta regla, aunque de otro modo aplicaría. La exclusión tiene prioridad sobre "Proveedores incluidos".
                </Typography>
                {providerRelationships.length === 0 ? (
                  <Alert severity="info" sx={{ mb: 1 }}>
                    Esta empresa no tiene proveedores activos vinculados.
                  </Alert>
                ) : (
                  <List dense sx={{ maxHeight: 220, overflow: 'auto', border: 1, borderColor: 'divider', borderRadius: 1 }}>
                    {providerRelationships.map((rel) => {
                      const provider = typeof rel.proveedorCompany === 'object' ? rel.proveedorCompany : null;
                      if (!provider) return null;
                      const checked = formData.excludedProviderCompanyIds.includes(provider._id);
                      return (
                        <ListItemButton key={provider._id} dense onClick={() => toggleExcludedProvider(provider._id)}>
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <Checkbox edge="start" checked={checked} disableRipple />
                          </ListItemIcon>
                          <ListItemText primary={provider.name} />
                        </ListItemButton>
                      );
                    })}
                  </List>
                )}
              </Box>
            )}

            {/* Company-level rules skip the "Proveedores incluidos" scoping
                entirely — pre-restricting eligibility and then separately
                picking one provider to request right now covered the same
                ground twice and could leave "Solicitar ahora" pointing at a
                provider the scoping had since excluded (Select then falls
                back to rendering the raw relationship id). The client
                decides per-relationship anyway via "Configurar
                verificaciones", so this is just the immediate-request
                shortcut against the full provider list. */}
            {formData.verificationLevel === 'company' && !editingVerification && (
              <Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={requestNow}
                      onChange={(e) => {
                        setRequestNow(e.target.checked);
                        if (!e.target.checked) setRequestNowRelationshipIds([]);
                      }}
                    />
                  }
                  label="Solicitar ahora a uno o más proveedores"
                />
                {requestNow && (
                  providerRelationships.length === 0 ? (
                    <Alert severity="info" sx={{ mt: 1 }}>
                      Esta empresa no tiene proveedores activos vinculados.
                    </Alert>
                  ) : (
                    <Box sx={{ mt: 1 }}>
                      <List dense sx={{ maxHeight: 220, overflow: 'auto', border: 1, borderColor: 'divider', borderRadius: 1 }}>
                        {providerRelationships.map((rel) => {
                          const provider =
                            typeof rel.proveedorCompany === 'object' ? rel.proveedorCompany : null;
                          if (!provider) return null;
                          const checked = requestNowRelationshipIds.includes(rel._id);
                          return (
                            <ListItemButton key={rel._id} dense onClick={() => toggleRequestNowRelationship(rel._id)}>
                              <ListItemIcon sx={{ minWidth: 36 }}>
                                <Checkbox edge="start" checked={checked} disableRipple />
                              </ListItemIcon>
                              <ListItemText primary={provider.name} />
                            </ListItemButton>
                          );
                        })}
                      </List>
                      <FormHelperText>
                        El administrador de cada proveedor marcado verá la solicitud de inmediato en "Mis Clientes".
                      </FormHelperText>
                    </Box>
                  )
                )}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{
          flexDirection: { xs: 'column-reverse', sm: 'row' },
          gap: { xs: 1, sm: 0 },
          p: { xs: 2, sm: 1 }
        }}>
          <Button
            onClick={handleCloseDialog}
            fullWidth={isXs}
            size={isMobile ? 'medium' : 'large'}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={!formData.name.trim()}
            fullWidth={isXs}
            size={isMobile ? 'medium' : 'large'}
            sx={primaryButtonSx}
          >
            {editingVerification ? 'Guardar cambios' : 'Crear verificación'}
          </Button>
        </DialogActions>
      </Dialog>

      <NotifyVerificationDialog
        open={!!notifyDialogVerification}
        companyId={companyId}
        verificationId={notifyDialogVerification?._id ?? null}
        verificationName={notifyDialogVerification?.name ?? ''}
        onClose={() => setNotifyDialogVerification(null)}
      />

      <Dialog
        open={userExclusionDialog.open}
        onClose={closeUserExclusionDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Excepciones de usuario</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Elige quién queda exento de "{userExclusionDialog.verification?.name}" aunque de otro
            modo le aplicaría. No afecta al resto de la empresa.
          </Typography>
          <Autocomplete
            multiple
            options={companyUsersForExclusion}
            getOptionLabel={(opt) => opt.name}
            isOptionEqualToValue={(opt, val) => opt._id === val._id}
            value={selectedExcludedUsers}
            loading={loadingExclusionUsers}
            onChange={(_, newValue, reason, details) => {
              const user = details?.option;
              if (!user) return;
              if (reason === 'selectOption') handleToggleExcludedUser(user, true);
              else if (reason === 'removeOption') handleToggleExcludedUser(user, false);
            }}
            renderOption={(props, opt) => (
              <li {...props} key={opt._id}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', minWidth: 0 }}>
                  <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>{opt.name}</Typography>
                  {opt.email && (
                    <Typography variant="caption" color="text.secondary" noWrap>{opt.email}</Typography>
                  )}
                  {togglingExcludedUserId === opt._id && <CircularProgress size={14} sx={{ ml: 'auto' }} />}
                </Box>
              </li>
            )}
            renderTags={(value, getTagProps) =>
              value.map((opt, index) => (
                <Chip {...getTagProps({ index })} key={opt._id} label={opt.name} />
              ))
            }
            renderInput={(params) => (
              <TextField {...params} label="Buscar usuarios de la empresa" placeholder="Nombre..." />
            )}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeUserExclusionDialog}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      {/* Provider counterpart of "Excepciones de usuario" above — same
          add/remove-by-Autocomplete shape, but toggles
          excludedProviderCompanyIds via updateVerification instead of the
          per-user toggle endpoint. */}
      <Dialog
        open={providerExclusionDialog.open}
        onClose={closeProviderExclusionDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Excepciones de proveedor</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Elige qué proveedores quedan exentos de "{providerExclusionDialog.verification?.name}" aunque de
            otro modo les aplicaría. Quitar a un proveedor de esta lista lo vuelve a incluir.
          </Typography>
          <Autocomplete
            multiple
            options={companyProvidersForExclusion}
            getOptionLabel={(opt) => opt.name}
            isOptionEqualToValue={(opt, val) => opt._id === val._id}
            value={selectedExcludedProviders}
            loading={loadingExclusionProviders}
            onChange={(_, newValue, reason, details) => {
              const provider = details?.option;
              if (!provider) return;
              if (reason === 'selectOption') handleToggleExcludedProvider(provider, true);
              else if (reason === 'removeOption') handleToggleExcludedProvider(provider, false);
            }}
            renderOption={(props, opt) => (
              <li {...props} key={opt._id}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', minWidth: 0 }}>
                  <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>{opt.name}</Typography>
                  {togglingExcludedProviderId === opt._id && <CircularProgress size={14} sx={{ ml: 'auto' }} />}
                </Box>
              </li>
            )}
            renderTags={(value, getTagProps) =>
              value.map((opt, index) => (
                <Chip {...getTagProps({ index })} key={opt._id} label={opt.name} />
              ))
            }
            renderInput={(params) => (
              <TextField {...params} label="Buscar proveedores vinculados" placeholder="Nombre..." />
            )}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeProviderExclusionDialog}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      {/* Row click → who this rule currently applies to. "Vinculados" tab:
          one-click "Quitar" excludes/unrequires them going forward.
          "Agregar" tab: search + add someone who wouldn't otherwise get the
          rule by scope. "Excepciones" reuses the dedicated exclusion dialog
          (stacks on top — same pattern already used elsewhere in this file). */}
      <Dialog
        open={linkedDialog.open}
        onClose={closeLinkedDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LinkedPeopleIcon fontSize="small" />
          {linkedDialog.verification?.verificationLevel === 'company'
            ? 'Proveedores vinculados'
            : 'Personas vinculadas'}
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
            <Tabs
              value={linkedTab}
              onChange={(_, v) => setLinkedTab(v)}
              sx={{ minHeight: 36, '& .MuiTab-root': { minHeight: 36, py: 0.5 } }}
            >
              <Tab label={newlyLinkedIds.size > 0 ? `Vinculados (${newlyLinkedIds.size})` : 'Vinculados'} />
              <Tab label="Agregar" icon={<PersonAddIcon fontSize="small" />} iconPosition="start" />
            </Tabs>
            <Button
              size="small"
              startIcon={<PersonOffIcon fontSize="small" />}
              onClick={() =>
                linkedDialog.verification &&
                (linkedDialog.verification.verificationLevel === 'company'
                  ? openProviderExclusionDialog(linkedDialog.verification)
                  : openUserExclusionDialog(linkedDialog.verification))
              }
            >
              Excepciones
            </Button>
          </Box>

          {linkedTab === 0 && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {linkedDialog.verification?.verificationLevel === 'company'
                  ? `Proveedores a los que "${linkedDialog.verification?.name}" les aplica actualmente.`
                  : `Personas a las que "${linkedDialog.verification?.name}" les aplica actualmente.`}
                {' '}Quitar a alguien lo exime de esta verificación sin afectar al resto.
              </Typography>

              {linkedLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress size={28} />
                </Box>
              ) : linkedDialog.verification?.verificationLevel === 'company' ? (
                linkedProviders.length === 0 ? (
                  <Alert severity="info">
                    Ningún proveedor tiene esta verificación vinculada actualmente.
                  </Alert>
                ) : (
                  <List dense disablePadding>
                    {linkedProviders.map((p) => (
                      <ListItem
                        key={p.id}
                        divider
                        secondaryAction={
                          <Tooltip title="Quitar de esta verificación">
                            <span>
                              <IconButton
                                edge="end"
                                size="small"
                                color="error"
                                disabled={removingLinkedId === p.id}
                                onClick={() => handleRemoveLinkedProvider(p)}
                              >
                                {removingLinkedId === p.id ? (
                                  <CircularProgress size={16} />
                                ) : (
                                  <PersonRemoveIcon fontSize="small" />
                                )}
                              </IconButton>
                            </span>
                          </Tooltip>
                        }
                      >
                        <ListItemAvatar>
                          <Avatar sx={{ bgcolor: 'action.hover' }}>
                            <BusinessIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={p.name}
                          secondary={LINKED_STATUS_LABEL[p.status] || p.status}
                        />
                      </ListItem>
                    ))}
                  </List>
                )
              ) : linkedIndividuals.length === 0 ? (
                <Alert severity="info">
                  Nadie tiene esta verificación vinculada actualmente.
                </Alert>
              ) : (
                <List dense disablePadding>
                  {linkedIndividuals.map((u) => (
                    <ListItem
                      key={u.id}
                      divider
                      secondaryAction={
                        <Tooltip title="Quitar de esta verificación">
                          <span>
                            <IconButton
                              edge="end"
                              size="small"
                              color="error"
                              disabled={removingLinkedId === u.id}
                              onClick={() => handleRemoveLinkedIndividual(u.id)}
                            >
                              {removingLinkedId === u.id ? (
                                <CircularProgress size={16} />
                              ) : (
                                <PersonRemoveIcon fontSize="small" />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                      }
                    >
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: 'action.hover' }}>
                          <PersonIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={u.name}
                        secondary={
                          <>
                            {u.email}
                            {u.email && ' · '}
                            {LINKED_STATUS_LABEL[u.status] || u.status}
                          </>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </>
          )}

          {linkedTab === 1 && linkedDialog.verification?.verificationLevel === 'company' && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Sume un proveedor vinculado a esta empresa para exigirle "{linkedDialog.verification?.name}",
                aunque de otro modo no le tocaría por el alcance de la regla.
              </Typography>
              <Autocomplete
                options={providerRelationships.filter((rel) => {
                  const pc = typeof rel.proveedorCompany === 'object' ? rel.proveedorCompany : null;
                  return !!pc && !linkedProviders.some((lp) => lp.id === pc._id);
                })}
                getOptionLabel={(rel) => (typeof rel.proveedorCompany === 'object' ? rel.proveedorCompany.name : '')}
                isOptionEqualToValue={(opt, val) => opt._id === val._id}
                value={null}
                inputValue={addProviderInput}
                onInputChange={(_, v) => setAddProviderInput(v)}
                loading={loadingProviderRelationships}
                onChange={(_, rel) => {
                  if (rel) {
                    handleAddLinkedProvider(rel);
                    setAddProviderInput('');
                  }
                }}
                renderOption={(props, rel) => {
                  const pc = typeof rel.proveedorCompany === 'object' ? rel.proveedorCompany : null;
                  return (
                    <li {...props} key={rel._id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', minWidth: 0 }}>
                        <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>{pc?.name}</Typography>
                        {addingLinkedId === rel._id && <CircularProgress size={14} sx={{ ml: 'auto' }} />}
                      </Box>
                    </li>
                  );
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Buscar proveedor vinculado" placeholder="Nombre..." />
                )}
              />
            </Box>
          )}

          {linkedTab === 1 && linkedDialog.verification?.verificationLevel !== 'company' && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Sume una persona a esta verificación aunque de otro modo no le tocaría por el alcance de la regla.
              </Typography>
              <Autocomplete
                options={companyUsersForExclusion.filter((u) => !linkedIndividuals.some((li) => li.id === u._id))}
                getOptionLabel={(opt) => opt.name}
                isOptionEqualToValue={(opt, val) => opt._id === val._id}
                value={null}
                inputValue={addPersonInput}
                onInputChange={(_, v) => setAddPersonInput(v)}
                loading={loadingExclusionUsers}
                onChange={(_, user) => {
                  if (user) {
                    handleAddLinkedIndividual(user);
                    setAddPersonInput('');
                  }
                }}
                renderOption={(props, opt) => (
                  <li {...props} key={opt._id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', minWidth: 0 }}>
                      <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>{opt.name}</Typography>
                      {opt.email && (
                        <Typography variant="caption" color="text.secondary" noWrap>{opt.email}</Typography>
                      )}
                      {addingLinkedId === opt._id && <CircularProgress size={14} sx={{ ml: 'auto' }} />}
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField {...params} label="Buscar usuarios de la empresa" placeholder="Nombre..." />
                )}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeLinkedDialog}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        fullScreen={isXs}
        sx={{
          '& .MuiDialog-paper': {
            margin: { xs: 0, sm: '32px' },
            width: { xs: '100%', sm: 'auto' },
            maxHeight: { xs: '100%', sm: '90vh' }
          }
        }}
      >
        <DialogTitle>Desactivar verificación</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            ¿Desactivar "{verificationToDelete?.name}"?
          </Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            <strong>Mientras esté desactivada:</strong>
            <ul style={{ marginTop: 4, marginBottom: 0, paddingLeft: 18 }}>
              <li>No será requerida para ningún colaborador.</li>
              <li>No afectará el estado de verificación de nadie.</li>
              <li>Los archivos ya subidos se conservan.</li>
              <li>Puede reactivarla en cualquier momento.</li>
            </ul>
          </Alert>
          <Alert severity="warning">
            Pasadas <strong>72 horas</strong>, un Super Admin o Soporte Monitor podrá borrarla
            permanentemente. El borrado permanente elimina la regla, todos los envíos de los
            colaboradores, y los archivos asociados — esa acción no se puede deshacer.
          </Alert>
        </DialogContent>
        <DialogActions sx={{
          flexDirection: { xs: 'column-reverse', sm: 'row' },
          gap: { xs: 1, sm: 0 },
          p: { xs: 2, sm: 1 }
        }}>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            fullWidth={isXs}
            size={isMobile ? 'medium' : 'large'}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleDelete}
            fullWidth={isXs}
            size={isMobile ? 'medium' : 'large'}
          >
            Desactivar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Hard-delete dialog — gated by super_admin/safety_staff, strong
          warning, type-to-confirm, optional note, optional force-bypass
          of the 72hr grace. */}
      <Dialog
        open={hardDeleteDialog.open}
        onClose={() =>
          !hardDeleteDialog.submitting &&
          setHardDeleteDialog({
            open: false,
            verification: null,
            note: '',
            confirmText: '',
            forceBypass: false,
            submitting: false,
            error: null,
          })
        }
        fullScreen={isXs}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: 'error.main' }}>
          Borrar verificación definitivamente
        </DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            <strong>Esta acción no se puede deshacer.</strong> Borra de forma permanente:
            <ul style={{ marginTop: 4, marginBottom: 0, paddingLeft: 18 }}>
              <li>La regla de verificación "{hardDeleteDialog.verification?.name}"</li>
              <li>Todos los envíos de los colaboradores (aprobados, rechazados, pendientes)</li>
              <li>Todos los archivos asociados en el almacenamiento</li>
              <li>El historial completo de re-envíos</li>
            </ul>
          </Alert>

          {hardDeleteDialog.verification?.deactivatedAt &&
          !isGraceElapsed(hardDeleteDialog.verification.deactivatedAt) ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Tiempo de gracia activo —{' '}
              <strong>
                {formatGraceRemaining(hardDeleteDialog.verification.deactivatedAt)}
              </strong>
              . Active la opción "Borrar ahora" para saltar el tiempo de gracia.
            </Alert>
          ) : null}

          <Typography variant="body2" sx={{ mb: 1 }}>
            Para confirmar, escriba el nombre exacto:{' '}
            <strong>{hardDeleteDialog.verification?.name}</strong>
          </Typography>
          <TextField
            fullWidth
            size="small"
            value={hardDeleteDialog.confirmText}
            onChange={(e) =>
              setHardDeleteDialog((s) => ({ ...s, confirmText: e.target.value }))
            }
            placeholder="Escriba el nombre"
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            multiline
            rows={2}
            size="small"
            label="Nota (opcional)"
            placeholder="Motivo del borrado, referencia de ticket, etc."
            value={hardDeleteDialog.note}
            onChange={(e) =>
              setHardDeleteDialog((s) => ({ ...s, note: e.target.value }))
            }
            sx={{ mb: 1 }}
          />

          {hardDeleteDialog.verification?.deactivatedAt &&
          !isGraceElapsed(hardDeleteDialog.verification.deactivatedAt) ? (
            canForceHardDelete ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                <input
                  type="checkbox"
                  id="force-bypass"
                  checked={hardDeleteDialog.forceBypass}
                  onChange={(e) =>
                    setHardDeleteDialog((s) => ({ ...s, forceBypass: e.target.checked }))
                  }
                />
                <label htmlFor="force-bypass">
                  <Typography variant="body2">
                    Borrar ahora (saltar tiempo de gracia)
                  </Typography>
                </label>
              </Box>
            ) : (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Solo super_admin o Soporte Monitor puede saltar el tiempo de gracia. Vuelva cuando termine la cuenta regresiva.
              </Typography>
            )
          ) : null}

          {hardDeleteDialog.error ? (
            <Alert severity="error" sx={{ mt: 2 }}>
              {hardDeleteDialog.error}
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions
          sx={{
            flexDirection: { xs: 'column-reverse', sm: 'row' },
            gap: { xs: 1, sm: 0 },
            p: { xs: 2, sm: 1 },
          }}
        >
          <Button
            onClick={() =>
              setHardDeleteDialog({
                open: false,
                verification: null,
                note: '',
                confirmText: '',
                forceBypass: false,
                submitting: false,
                error: null,
              })
            }
            disabled={hardDeleteDialog.submitting}
            fullWidth={isXs}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleHardDelete}
            disabled={
              hardDeleteDialog.submitting ||
              hardDeleteDialog.confirmText.trim() !==
                (hardDeleteDialog.verification?.name || '').trim() ||
              (hardDeleteDialog.verification?.deactivatedAt &&
                !isGraceElapsed(hardDeleteDialog.verification.deactivatedAt) &&
                !hardDeleteDialog.forceBypass) ||
              false
            }
            fullWidth={isXs}
          >
            {hardDeleteDialog.submitting ? 'Borrando…' : 'Borrar permanentemente'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};